import {
  Card,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  selectAccount,
  selectExpenses,
} from "../../redux/UserSlice";
import { UserCardInfo } from "../../components/UserCardInfo/UserCardInfo";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { FeatureIcon } from "../../utils/FeatureIcon";
import submit_expense from "../../img/logos_reissue.png";
import { useSelector } from "react-redux";
import { Redirect, useHistory } from "react-router-dom";
import theme, { dark_blue } from "../../theme";
import { ping } from "../../redux/UserAPI";
import EnhancedTableHead from "../../EnhancedTableHead";
import { Data, HeadCell, Order } from "../../interfaces";
import getComparator from "../../getComparator";

const paper_style = {
  padding: "0.5em",
};

export const table_style = {
  textAlign: "center",
  [theme.breakpoints.up("xs")]: {
    fontSize: 18,
  },
  [theme.breakpoints.up("md")]: {
    fontSize: 20,
  },
  [theme.breakpoints.up("xl")]: {
    fontSize: 25,
  },
};

export function UserPage() {
  const width = window.screen.width;
  const account = useAppSelector(selectAccount);
  const history = useHistory();
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof Data>("GRANT_DATE");
  const expenses = useSelector(selectExpenses);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  const dispatch = useAppDispatch();

  useEffect(() => {
    const getAccount = async () => {
      if (!account) {
        const res: any = await dispatch(ping("a"));
        console.log(res);
        if (res.error || !res.payload.data.result) {
          history.push("/");
        }
      }
    };
    getAccount();
  }, [account]);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const headCells: HeadCell[] = [
    {
      id: "GRANT_DATE",
      numeric: false,
      disablePadding: false,
      label: "Decision Date",
    },
    {
      id: "REQUEST_AMOUNT_IN_GRANT_CURRENCY",
      numeric: true,
      disablePadding: false,
      label: "Amount",
    },
    {
      id: "GRANT_TYPE",
      numeric: false,
      disablePadding: false,
      label: "Type",
    },
    {
      id: "COMMITTEE_APROVAL_DECISION",
      numeric: false,
      disablePadding: false,
      label: "Status",
    },
    {
      id: "APPROVED_AMOUNT_IN_GRANT_CURRENCY",
      numeric: true,
      disablePadding: false,
      label: "Approved Amount in Grant Currency",
    },
    {
      id: "PAID",
      numeric: false,
      disablePadding: false,
      label: "Paid",
    },
  ];
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const visibleRows = useMemo(
    () => stableSort(expenses, getComparator(order, orderBy)),
    [order, orderBy, expenses]
  );
  

  return (
    <Grid
      container
      spacing={1}
      justifyContent="space-evenly"
      sx={{
        marginBottom: "7em",
        padding: width < 600 ? 3 : 4,
        [theme.breakpoints.up("xl")]: {
          mt: "10em",
        },
        [theme.breakpoints.up("xs")]: {
          mt: "7em",
        },
      }}
      alignItems={"flex-start"}
    >
      <Grid item xs={12} md={5}>
        <Grid container direction={"row"}>
          <Grid item xs={8} md={12}>
            <Paper sx={paper_style}>
              <UserCardInfo />
            </Paper>
          </Grid>
          <Grid item xs={3} sx={{ m: "1em" }}>
            <FeatureIcon
              src={submit_expense}
              path={"/submit_expense"}
              title={"Submit an Expense"}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={7} sx={{ paddingTop: 0 }}>
        <Paper>
          <Typography variant={"h3"} sx={{ color: dark_blue }}>
            Expenses
          </Typography>
          <Typography sx={{ color: dark_blue, fontStyle: "italic" }}>
            Note: The expenses in the below table may not reflect recent
            submissions. It can take up to 7 days for expense submissions to be
            displayed here.
          </Typography>
          <TableContainer sx={{ overflow: "scroll", maxHeight: "35em" }}>
            <Table aria-label="simple table">
              <EnhancedTableHead
                headCells={headCells}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={expenses?.length ? expenses.length : 0}
              />

              <TableBody>
                {expenses.length
                  ? visibleRows.map((row, i) =>
                      row.REQUEST_AMOUNT_IN_GRANT_CURRENCY ? (
                        <TableRow
                          component={Card}
                          sx={{ m: "1em", height: "5em", pt: "2em" }}
                          elevation={6}
                          key={i}
                        >
                          <TableCell
                            align="center"
                            sx={{ border: 0, pr: "1em", padding: 0 }}
                          >
                            <Typography
                              component={"span"}
                              className="table_style"
                              color={"primary"}
                            >
                              {row.GRANT_DATE}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ border: 0, pr: "2em" }}
                          >
                            <Typography
                              component={"span"}
                              className="table_style"
                              color={"primary"}
                            >
                              ${" "}
                              {row.REQUEST_AMOUNT_IN_GRANT_CURRENCY?.toLocaleString(
                                "en-US"
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ border: 0, pr: "2em" }}
                          >
                            <Typography
                              component={"span"}
                              className="table_style"
                              color={"primary"}
                            >
                              {row.GRANT_TYPE}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ border: 0, pr: "2em" }}
                          >
                            <Typography
                              component={"span"}
                              className="table_style"
                              color={"primary"}
                            >
                              {row.COMMITTEE_APROVAL_DECISION}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ border: 0, pr: "2em" }}
                          >
                            <Typography
                              component={"span"}
                              className="table_style"
                              color={"primary"}
                            >
                              {row.APPROVED_AMOUNT_IN_GRANT_CURRENCY}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ border: 0, pr: "2em" }}
                          >
                            <Typography
                              component={"span"}
                              className="table_style"
                              color={"primary"}
                            >
                              {row.PAID}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        ""
                      )
                    )
                  : ""}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );
}
